import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Landmark, School, BookOpen, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import BrandMark from "@/components/BrandMark";
import { CollegeCard } from "@/components/directory/CollegeCard";
import { DirectoryFilters, Pick } from "@/components/directory/DirectoryFilters";
import { filterColleges, sortColleges, downloadCsv, SORTS } from "@/lib/directory";

const PAGE = 30;

const Header = () => (
  <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
    <Link to="/" className="flex items-center gap-2" data-testid="directory-home-link">
      <BrandMark size={44} />
      <span className="font-serif-display text-2xl font-semibold text-[color:var(--ayur-emerald)]">VaidGram</span>
    </Link>
    <Link to="/signup"><Button className="rounded-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)]" data-testid="directory-signup-btn">Join as a scholar</Button></Link>
  </header>
);

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="paper-card bg-white p-4 flex items-center gap-3" data-testid={testid}>
    <Icon className="w-5 h-5 text-[color:var(--ayur-terracotta)]" />
    <div><div className="text-xl font-semibold text-[color:var(--ayur-emerald)]">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--ayur-mute)] font-mono-brand">{label}</div></div>
  </div>
);

function useDirectory() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/directory/colleges").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);
  return { data, err };
}

export default function CollegeDirectory() {
  const { stateSlug } = useParams();
  const navigate = useNavigate();
  const { data, err } = useDirectory();
  const [f, setF] = useState({ q: "", course: "all", subject: "all", type: "all", sort: "default" });
  const [shown, setShown] = useState(PAGE);
  const all = data?.colleges || [];
  useEffect(() => { window.scrollTo(0, 0); }, [stateSlug]);
  const filters = { ...f, state: stateSlug || "" };

  const set = (patch) => {
    setShown(PAGE);
    if ("state" in patch) { navigate(patch.state ? `/ayurveda-colleges/${patch.state}` : "/ayurveda-colleges"); delete patch.state; }
    setF((p) => ({ ...p, ...patch }));
  };

  const states = useMemo(() => {
    const m = {};
    all.forEach((c) => { m[c.slug] = m[c.slug] || { slug: c.slug, state: c.state, count: 0 }; m[c.slug].count += 1; });
    return Object.values(m).sort((a, b) => a.state.localeCompare(b.state));
  }, [all]);
  const subjects = useMemo(() => {
    const m = {};
    all.forEach((c) => c.pg_subjects.forEach((s) => { m[s.name] = s; }));
    return Object.values(m).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);
  const list = useMemo(() => sortColleges(filterColleges(all, filters), f.sort), [all, f, stateSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const stateName = states.find((s) => s.slug === stateSlug)?.state;
  const invalidState = Boolean(data && stateSlug && !stateName);
  const place = stateName || "India";
  const ug = list.reduce((a, c) => a + c.ug_seats, 0);
  const pg = list.reduce((a, c) => a + c.pg_seats, 0);
  const govt = list.filter((c) => !/private|deemed/i.test(c.type)).length;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "ItemList",
    name: `NCISM-permitted Ayurveda (BAMS, MD, MS) colleges in ${place} 2026-27`,
    numberOfItems: list.length,
    itemListElement: list.slice(0, 100).map((c, i) => ({
      "@type": "ListItem", position: i + 1,
      item: { "@type": "CollegeOrUniversity", name: c.name, address: { "@type": "PostalAddress", streetAddress: c.address, addressRegion: c.state, addressCountry: "IN" } },
    })),
  };

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)] text-[color:var(--ayur-ink)]" data-testid="college-directory-page">
      <Seo
        title={`Ayurveda Colleges in ${place} 2026-27 — BAMS, MD & MS seats (NCISM list)`}
        description={`Complete NCISM-permitted list of ${list.length || ""} Ayurveda colleges in ${place} for 2026-27 admissions: BAMS seats, MD/MS Ayurveda PG subjects & seats, Govt/Private status and address.`}
        path={stateSlug ? `/ayurveda-colleges/${stateSlug}` : "/ayurveda-colleges"}
        keywords={`Ayurveda colleges in ${place}, BAMS colleges ${place}, BAMS admission 2026, MD Ayurveda colleges, MS Ayurveda colleges, NCISM permitted colleges list 2026-27, government BAMS colleges ${place}, private BAMS colleges, BAMS seats, AIAPGET colleges, Ayurveda PG seats`}
        jsonLd={jsonLd}
        noindex={invalidState}
      />
      <Header />
      <main className="max-w-7xl mx-auto px-6 pb-16">
        {invalidState && (
          <div className="mb-6 paper-card bg-white p-4 text-sm" data-testid="directory-invalid-state">
            We couldn't find the state "{stateSlug}". <Link to="/ayurveda-colleges" className="font-semibold text-[color:var(--ayur-emerald)] link-underline" data-testid="directory-invalid-state-link">View all Ayurveda colleges in India</Link>
          </div>
        )}
        <nav className="text-xs font-mono-brand text-[color:var(--ayur-mute)]" data-testid="directory-breadcrumb">
          <Link to="/" className="link-underline">Home</Link> / <Link to="/ayurveda-colleges" className="link-underline">Ayurveda colleges</Link>{stateName && <> / {stateName}</>}
        </nav>
        <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--ayur-emerald)] leading-tight" data-testid="directory-title">
          Ayurveda colleges in {place}
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-[color:var(--ayur-ink-2)] leading-relaxed">
          Official list of Ayurveda colleges granted permission by NCISM for the academic year 2026-27 — BAMS (UG) intake, MD & MS (Ayurveda) PG subjects and seats, ownership type and full address. Filter by state, course and specialty to shortlist colleges for BAMS admission or AIAPGET counselling.
        </p>
        {data && <p className="mt-2 text-xs font-mono-brand text-[color:var(--ayur-mute)]" data-testid="directory-source">Source: {data.source} · status as of {data.as_of}</p>}

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={School} label="Colleges" value={list.length} testid="kpi-colleges" />
          <Kpi icon={Landmark} label="Govt / aided" value={govt} testid="kpi-govt" />
          <Kpi icon={BookOpen} label="BAMS seats" value={ug.toLocaleString("en-IN")} testid="kpi-ug" />
          <Kpi icon={Users} label="MD/MS seats" value={pg.toLocaleString("en-IN")} testid="kpi-pg" />
        </div>

        <div className="mt-8"><DirectoryFilters f={filters} set={set} states={states} subjects={subjects} /></div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-[color:var(--ayur-ink-2)]" data-testid="directory-result-count">{list.length} colleges found</span>
          <div className="flex items-center gap-2">
            <div className="w-48"><Pick value={f.sort} onChange={(v) => set({ sort: v })} options={SORTS} testid="directory-sort-select" /></div>
            <Button variant="outline" size="sm" onClick={() => downloadCsv(list, `ayurveda-colleges-${stateSlug || "india"}-2026-27.csv`)} disabled={!list.length} data-testid="directory-download-csv">
              <Download className="w-4 h-4 mr-1" /> Download CSV
            </Button>
          </div>
        </div>

        {err && <p className="mt-10 text-sm text-red-700" data-testid="directory-error">Could not load the directory. Please refresh.</p>}
        {!data && !err && <p className="mt-10 text-sm text-[color:var(--ayur-mute)]" data-testid="directory-loading">Loading colleges…</p>}
        {data && !list.length && <p className="mt-10 text-sm text-[color:var(--ayur-mute)]" data-testid="directory-empty">No colleges match these filters.</p>}
        <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3 items-start" data-testid="directory-list">
          {list.slice(0, shown).map((c) => <CollegeCard key={c.college_id + c.sno} c={c} />)}
        </div>
        {shown < list.length && (
          <div className="mt-8 flex justify-center">
            <Button variant="outline" className="rounded-full" onClick={() => setShown(shown + PAGE)} data-testid="directory-load-more">Show more ({list.length - shown} left)</Button>
          </div>
        )}

        <section className="mt-14 paper-card bg-[color:var(--ayur-emerald)] text-white p-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="directory-signup-nudge">
          <div>
            <h2 className="font-serif-display text-2xl">Studied at one of these colleges?</h2>
            <p className="mt-1 text-sm text-white/80">Join VaidGram to find your batchmates, seniors and Gurus from your BAMS college.</p>
          </div>
          <Link to="/signup"><Button className="rounded-full bg-[color:var(--ayur-gold)] text-[color:var(--ayur-ink)] hover:bg-white" data-testid="directory-nudge-signup-btn">Create free scholar profile</Button></Link>
        </section>

        <section className="mt-12" data-testid="directory-state-links">
          <h2 className="font-serif-display text-xl text-[color:var(--ayur-emerald)]">Browse Ayurveda colleges by state</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {states.map((s) => (
              <Link key={s.slug} to={`/ayurveda-colleges/${s.slug}`} onClick={() => setShown(PAGE)} data-testid={`state-link-${s.slug}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${s.slug === stateSlug ? "bg-[color:var(--ayur-emerald)] text-white border-transparent" : "border-[color:var(--ayur-border)] bg-white hover:border-[color:var(--ayur-emerald)]"}`}>
                BAMS colleges in {s.state} ({s.count})
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
